import {
  makeHttpClient,
  _unwrapAxiosResponse,
  STORAGE_KEY_ACCESS_TOKEN,
  STORAGE_KEY_ACCESS_TOKEN_TYPE,
  STORAGE_KEY_REFRESH_TOKEN,
  STORAGE_KEY_VALID_UNTIL
} from "./http";
import { catchError, map, mergeAll, mergeMap, tap } from "rxjs/operators";
import { combineLatest, of } from "rxjs";
import VueRouter from "vue-router";
import env from "../environment/env.json";

export interface Circul8Account {
  id: string;
  username: string;
  email: string;
  fullName: string;
  firstName: string;
  lastName: string;
}

export interface LoginStatusResult {
  loggedIn: boolean;
  token?: string;
  tokenValidUntil?: number;
  account?: Circul8Account;
  ssoTokenExpired: boolean;
}
export interface PermissionsResult {
  id?: number;
  isExternal?: boolean;
  groups?: string[];
  permissions?: string[];
  tenant_exists?: boolean;
  isSuccessful?: boolean;
  mappedCustomer?: string[]; // list of customer ids if user is external else empty for internal user
  error?: any;
}

export interface TokenResponse {
  /** eslint-disable camelcase */
  access_token?: string;
  token_type?: string;
  expires_in?: number;
  ext_expires_in?: number;
  scope?: string;
  refresh_token?: string;
  error_description?: string;
  error_codes?: number[];
  timestamp?: string;
  trace_id?: string;
  correlation_id?: string;
  /** eslint-enable camelcase */
}

class AuthService {
  /**
   * TODO: 2020-12-08, gf:
   * Remove baseURL option alltogether once SNG-292 is done and we are not working with IP addresses and port numbers locally anymore.
   */
  private httpClient = makeHttpClient({
    baseURL:
      env.scoreApiUrl.indexOf("127.0.0.1") != -1 ? env.scoreApiUrl : env.ssoUrl
  });
  private httpEprClient = makeHttpClient();

  /** eslint-disable-next-line @typescript-eslint/no-explicit-any */
  login(appComponentsData: any) {
    const state = [...Array(10)]
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .map(_ => ((Math.random() * 36) | 0).toString(36))
      .join("");
    localStorage.setItem(
      `state-${state}`,
      JSON.stringify(
        Object.assign(
          { url: window.location.pathname, time: new Date().getTime() },
          appComponentsData
        )
      )
    );

    const params: {
      /** eslint-disable camelcase, @typescript-eslint/camelcase */
      state: string;
      client_id: string;
      redirect_uri: string;
    } = {
      state,
      client_id: "epr",
      redirect_uri: `${env.eprUrl}/auth/callback` // env.auth.redirectUri
      /** eslint-disable camelcase, @typescript-eslint/camelcase */
    };

    const url = env.ssoUrl + "/authorize?" + params;
    console.log("Starting AUTH request to URL: " + url);
    window.location.href = url;
  }

  deleteAllCookies() {
    const cookies = document.cookie.split("; ");
    for (let c = 0; c < cookies.length; c++) {
      const d = window.location.hostname.split(".");
      while (d.length > 0) {
        const cookieBase =
          encodeURIComponent(cookies[c].split(";")[0].split("=")[0]) +
          "=; expires=Thu, 01-Jan-1970 00:00:01 GMT; domain=" +
          d.join(".") +
          " ;path=";
        const p = location.pathname.split("/");
        document.cookie = cookieBase + "/";
        while (p.length > 0) {
          document.cookie = cookieBase + p.join("/");
          p.pop();
        }
        d.shift();
      }
    }
  }

  logout(keycloak: any) {
    localStorage.clear();
    this.deleteAllCookies();

    keycloak.logout({
      post_logout_redirect_uri: env.eprUrl,
      client_id: "scoreng-frontend"
      // redirectUri: env.eprUrl
    });
  }

  whoami() {
    const permissions = this.getPermissions();
    return combineLatest([permissions]);
  }

  getPermissions() {
    const switchCustomerCache = localStorage.getItem("switchCustomerView");
    let switchCustomerId: number | null = null;

    if (switchCustomerCache) {
      try {
        const switchCustomerData = JSON.parse(switchCustomerCache);
        if (
          switchCustomerData?.isSwitchCustomerView &&
          switchCustomerData?.switchCustomerNumber
        ) {
          switchCustomerId = Number(switchCustomerData.switchCustomerNumber);
        }
      } catch (error) {
        console.warn("Invalid switchCustomerView cache", error);
      }
    }

    const requestConfig =
      switchCustomerId != null
        ? {
            params: {
              customer_id: switchCustomerId
            }
          }
        : undefined;

    return _unwrapAxiosResponse(
      this.httpClient.get<PermissionsResult>("/api/permission/", requestConfig)
    ).pipe(
      map((response: any) => {
        // Check if response is HTML instead of JSON
        if (
          typeof response === "string" &&
          response.trim().startsWith("<!DOCTYPE")
        ) {
          return {
            error: {
              isHtmlError: true,
              message: "Server returned HTML instead of JSON"
            }
          } as PermissionsResult;
        }
        return response;
      }),
      catchError((err: any) => of({ error: err } as PermissionsResult)),
      tap(permissionResult => {
        permissionResult.isSuccessful =
          !!permissionResult.id && !permissionResult.error;
      })
    );
  }

  restoreStateAndRedirect(stateKey: string, router: VueRouter) {
    const storedDataJson = localStorage.getItem("state-" + stateKey);
    if (!storedDataJson) {
      router.push({ name: "home" });
      return;
    }
    localStorage.removeItem("state-" + stateKey);
    const storedData = JSON.parse(storedDataJson);
    if (storedData && storedData.url) {
      window.location.href = storedData.url;
    } else {
      router.push({ name: "home" });
    }
  }
}

export { AuthService };
